// noinspection ES6UnusedImports
import React, {useEffect, useState} from 'react';
import MessageBroker from '@bonprix/pattern-library/components/message-broker';
import ProductMessages from './message-list';
import {emailFormatValidator} from '@core/scripts/helper/validation/email-validator';
import {hasError, maxLengthValidator, validatorOf} from '@core/scripts/helper/validation/validators';
import AppAvailabilityReminder from '@core/scripts/components/actions/app-availability-reminder';
import {BottomSheet, Button, Dialog, Typography} from "@bonprix/design-system";
import Landmark from "@core/scripts/components/landmark-adapter";
import submitAvailabilityReminderHandler from "@core/scripts/components/actions/submit-availability-reminder-handler";
import Breakpoint from '@core/scripts/helper/breakpoint';
import AvailabilityReminderContent from "@core/scripts/components/change-item/AvailabilityReminderContent.jsx";

const MOBILE_DEVICE_BREAKPOINTS = ['start', 'small'];
const DESKTOP_DEVICE_BREAKPOINTS = ['medium', 'large', 'xlarge', 'max'];

export default function AvailabilityReminderLayer({texts, product, email}) {
    const [showReminderLayer, setShowReminderLayer] = useState(false);
    const [showSuccessLayer, setShowSuccessLayer] = useState(false);
    const [currentItem, setCurrentItem] = useState(product.currentItem);
    const [currentItemOption, setCurrentItemOption] = useState(product.currentItemOption);
    const [availabilityReminderEmail, setAvailabilityReminderEmail] = useState(email);
    const [newsletterSubscriptionErrors, setNewsletterSubscriptionErrors] = useState(null);
    const [isEmailError, setIsEmailError] = useState(false);
    const [isNewsletter, setIsNewsletter] = useState(false);
    const [facadeError, setFacadeError] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [autoCloseTimerMs, setAutoCloseTimerMs] = useState(null);

    useEffect(() => {
        const subscriptions = [
            MessageBroker.subscribe(ProductMessages.AVAILABILITY_REMINDER_LAYER_CHANGED, ({open}) => {
                setShowReminderLayer(open);
            }),
            MessageBroker.subscribe(ProductMessages.CHANGED, ({item, itemOption}) => {
                if (item !== undefined) {
                    setCurrentItem(item);
                }
                if (itemOption !== undefined) {
                    setCurrentItemOption(itemOption);
                }
                setShowSuccessLayer(false);
            }),
        ];
        return () =>
            subscriptions.forEach((subscription) => {
                return MessageBroker.unsubscribe(subscription);
            });
    }, []);

    function handleAutoCloseTimer() {
        if (Breakpoint.keyIsIn(MOBILE_DEVICE_BREAKPOINTS)) {
            setAutoCloseTimerMs(5000);
        } else {
            setTimeout(() => setShowReminderLayer(false), 5000);
        }
    }

    const showAvailabilityReminderSuccess = (data, email) => {
        const {success, newsletterSubscriptionResponse, supportsAppAvailabilityReminder} = data;
        if (supportsAppAvailabilityReminder) {
            AppAvailabilityReminder.notify(email);
            setShowReminderLayer(false);
        } else {
            setShowSuccessLayer(success);
            if (success) {
                handleAutoCloseTimer();
            }
        }
        setAvailabilityReminderEmail(email);
        setNewsletterSubscriptionErrors(newsletterSubscriptionResponse?.errors);
    };

    const onCloseLayer = () => {
        setShowReminderLayer(false);
        setShowSuccessLayer(false);
        setSubmitting(false);
        setNewsletterSubscriptionErrors(null);
        setAutoCloseTimerMs(null);
    };

    const onTextInput = (value) => {
        setAvailabilityReminderEmail(value);
        setIsEmailError(false);
    };

    const onCheckboxChange = () => setIsNewsletter(!isNewsletter);

    const validateEmailFormat = emailFormatValidator();
    const emailValidator = validatorOf({format: validateEmailFormat, maxLength: maxLengthValidator(75)});

    const successHandler = (data, email) => {
        setFacadeError(!data.success);
        setSubmitting(false);
        showAvailabilityReminderSuccess(data, email);
    };

    const submitAvailabilityReminder = () => {
        setFacadeError(false);

        const validationResult = emailValidator.validate(availabilityReminderEmail);
        const hasEmailError = hasError(validationResult);
        setIsEmailError(hasEmailError)

        if (hasEmailError) {
            return;
        }

        setSubmitting(true);
        sendAvailabilityReminderLandmark();

        return submitAvailabilityReminderHandler(
            availabilityReminderEmail,
            isNewsletter,
            product.styleId,
            currentItem.id,
            currentItemOption.id,
            currentItemOption?.price.unified.value,
            successHandler
        );
    };

    const sendAvailabilityReminderLandmark = () => {
        const {channelId, shopId, masterStyleId, styleId} = product;
        const landmarkValue =
            `adp\\availability_mail\\send\\${currentItem?.itemNumber}\\\\${channelId}\\${shopId}` +
            `\\${masterStyleId}\\${styleId || ''}\\${currentItemOption.id || currentItem.id}`;
        Landmark.sendClickLandmark(landmarkValue);
    };

    const showBottomSheet = Breakpoint.keyIsIn(MOBILE_DEVICE_BREAKPOINTS);
    const showDialog = Breakpoint.keyIsIn(DESKTOP_DEVICE_BREAKPOINTS);

    const headline = showSuccessLayer
        ? texts.bff_wishlist_availability_reminder_success_headline
        : texts.bff_wishlist_availability_reminder_headline;

    const content = (
        <AvailabilityReminderContent facadeError={facadeError} showSuccessLayer={showSuccessLayer}
                                     currentItem={currentItem} currentItemOption={currentItemOption}
                                     availabilityReminderEmail={availabilityReminderEmail}
                                     texts={texts} isEmailError={isEmailError}
                                     newsletterSubscriptionErrors={newsletterSubscriptionErrors}
                                     isNewsletter={isNewsletter} onTextInput={onTextInput}
                                     onCheckboxChange={onCheckboxChange}/>
    );

    const submitButton = (
        <Button onClick={submitAvailabilityReminder} variant="primary" loading={submitting}>
            {texts.bff_wishlist_availability_reminder_button}
        </Button>
    );
    const closeButton = (
        <Button variant="secondary" onClick={onCloseLayer}>
            {texts.close_layer}
        </Button>
    );
    const shoppingButton = (
        <Button variant="primary" onClick={onCloseLayer}>
            {texts.ada_go_on_shopping_button}
        </Button>
    );

    return (
        <>
            {/* OPENS IN BOTTOM SHEET FOR MOBILE */}
            {showBottomSheet && (
                <BottomSheet
                    isOpen={showReminderLayer}
                    onClose={onCloseLayer}
                    onSnap={onCloseLayer}
                    autoClose={autoCloseTimerMs}
                >
                    <div className="availability-reminder__layer">
                        <div className="availability-reminder__headline">
                            <Typography variant="bonprix-h4">{headline}</Typography>
                        </div>
                        {content}
                        <div className="availability-reminder__buttons">
                            {!showSuccessLayer && submitButton}
                            {!showSuccessLayer && closeButton}
                            {showSuccessLayer && shoppingButton}
                        </div>
                    </div>
                </BottomSheet>
            )}

            {/* OPENS IN DIALOG FOR DESKTOP */}
            {showDialog && (
                <Dialog zIndex={1000} isOpen={showReminderLayer} onClose={onCloseLayer}>
                    <Dialog.Title>{headline}</Dialog.Title>
                    <Dialog.Content>{content}</Dialog.Content>
                    {!showSuccessLayer && <Dialog.PrimaryButton fullWidth>{submitButton}</Dialog.PrimaryButton>}
                    {!showSuccessLayer && <Dialog.SecondaryButton fullWidth>{closeButton}</Dialog.SecondaryButton>}
                    {showSuccessLayer && <Dialog.PrimaryButton fullWidth>{shoppingButton}</Dialog.PrimaryButton>}
                </Dialog>
            )}
        </>
    );
}
